import { getCountryCodes } from '@/shared/constants';
import type { TCountryCode } from '@/shared/types';
import { getCookie } from '@/shared/utils/cookies';
import { useTranslation } from '@crehana/i18n';
import { useMemo } from 'react';

/**
 * Hook to get the default country code based on cookie or fallback to default
 * @param countryCodes - Array of country codes to search in
 * @param defaultCode - Default country code to use if cookie is not found (default: '+57')
 * @returns The default country code value
 */
const useDefaultCountryCode = (
  countryCodes: TCountryCode[],
  defaultCode: string = '+57',
): string => {
  return useMemo(() => {
    const cookieCountryCode = getCookie('__creh_country_code');
    if (cookieCountryCode) {
      if (cookieCountryCode.length === 2) {
        const foundCountry = countryCodes.find(
          country => country.iso === cookieCountryCode.toUpperCase(),
        );
        if (foundCountry) {
          return foundCountry.value;
        }
      }

      const foundCountry = countryCodes.find(
        country =>
          country.value === `+${cookieCountryCode}` ||
          country.value === cookieCountryCode,
      );
      if (foundCountry) {
        return foundCountry.value;
      }
    }
    return defaultCode;
  }, [countryCodes, defaultCode]);
};

/**
 * Hook to get country codes and default country code based on language
 * Combines getCountryCodes and useDefaultCountryCode for convenience
 * @param defaultCode - Default country code to use if cookie is not found (default: '+57')
 * @returns Object with countryCodes array and defaultCountryCode string
 */
export const useCountryCodesWithDefault = (
  defaultCode: string = '+57',
): { countryCodes: TCountryCode[]; defaultCountryCode: string } => {
  const { languageCode } = useTranslation();

  const countryCodes = useMemo(() => {
    const validLanguages = ['es', 'en', 'pt'] as const;
    type TValidLanguage = (typeof validLanguages)[number];

    const isValidLanguage = (lang: string): lang is TValidLanguage => {
      return (validLanguages as readonly string[]).includes(lang);
    };

    const validLanguage = isValidLanguage(languageCode) ? languageCode : 'es';
    return getCountryCodes(validLanguage);
  }, [languageCode]);

  const defaultCountryCode = useDefaultCountryCode(countryCodes, defaultCode);

  return { countryCodes, defaultCountryCode };
};
